/**
 * EmojiInputElement - Inline combobox for emoji autocomplete.
 *
 * Uses Plate's InlineCombobox pattern with the shared EmojiInlineIndexSearch
 * singleton (from @platejs/emoji) to search the lazily-loaded emoji-mart
 * dataset.
 */

import {
  type Emoji,
  EmojiInlineIndexSearch,
  insertEmoji,
} from '@platejs/emoji';
import type { PlateElementProps } from 'platejs/react';
import { useEditorRef } from 'platejs/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  InlineCombobox,
  InlineComboboxContent,
  InlineComboboxInput,
  InlineComboboxItem,
} from '@/lib/crm/components/inline-combobox';

import { type EmojiMartData, loadEmojiData } from './emoji-data';

const MAX_EMOJI_RESULTS = 20;

// eslint-disable-next-line react-refresh/only-export-components -- pure helper co-located with its component for direct unit testing
export function searchEmojis(
  data: EmojiMartData | null,
  rawValue: string
): Emoji[] {
  const query = rawValue.trim().replace(/:$/, '');
  // Hard guard: never reach getInstance until the real dataset resolves.
  // getInstance is first-call-wins; an early call with the 1-emoji stub
  // poisons the page-lifetime index (decision #2).
  if (!data || query.length === 0) {
    return [];
  }
  // O(1) per keystroke ONLY because getInstance is a first-call-wins
  // singleton and `data` is stable state — never pass a freshly-built object.
  return EmojiInlineIndexSearch.getInstance(data)
    .search(query)
    .get()
    .slice(0, MAX_EMOJI_RESULTS);
}

export interface QueryMatchParts {
  after: string;
  before: string;
  match: string;
}

/**
 * Locate the typed query inside a candidate string (an emoji shortcode or a
 * keyword) so the matched substring can be highlighted (Slack-style, e.g. the
 * "plus" in :heavy_plus_sign:). Case-insensitive; the query is normalized the
 * same way as searchEmojis. Returns null when the query doesn't appear.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper co-located with its component for direct unit testing
export function splitQueryMatch(
  candidate: string,
  rawQuery: string
): QueryMatchParts | null {
  const query = rawQuery.trim().replace(/:$/, '').toLowerCase();
  if (query.length === 0) {
    return null;
  }
  const index = candidate.toLowerCase().indexOf(query);
  if (index === -1) {
    return null;
  }
  return {
    after: candidate.slice(index + query.length),
    before: candidate.slice(0, index),
    match: candidate.slice(index, index + query.length),
  };
}

/**
 * For rows the search surfaced through a KEYWORD rather than the shortcode
 * itself (":lor" → :crown: via "lord"), find the first matching keyword so
 * the row can explain why it appeared. Returns null when the shortcode match
 * already explains the row, or no keyword contains the query (name matches).
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper co-located with its component for direct unit testing
export function findKeywordMatch(
  keywords: string[],
  rawQuery: string
): QueryMatchParts | null {
  for (const keyword of keywords) {
    const parts = splitQueryMatch(keyword, rawQuery);
    if (parts) {
      return parts;
    }
  }
  return null;
}

function HighlightedMatch({ parts }: { parts: QueryMatchParts }) {
  return (
    <>
      {parts.before}
      <span className='font-semibold bg-utility-warning-100 rounded-[2px]'>
        {parts.match}
      </span>
      {parts.after}
    </>
  );
}

interface EmojiComboboxItemProps {
  emoji: Emoji;
  onSelect: (emoji: Emoji) => void;
  query: string;
}

function EmojiComboboxItem({ emoji, onSelect, query }: EmojiComboboxItemProps) {
  const handleClick = useCallback(() => onSelect(emoji), [emoji, onSelect]);
  const shortcodeParts = splitQueryMatch(emoji.id, query);
  // Only look for a keyword explanation when the shortcode itself doesn't
  // contain the query (":lor" → :crown: matched via the keyword "lord").
  const keywordParts = shortcodeParts
    ? null
    : findKeywordMatch(emoji.keywords, query);
  return (
    <InlineComboboxItem
      value={emoji.id}
      onClick={handleClick}
      className='flex items-center gap-2'
    >
      <span className='text-base'>{emoji.skins[0]?.native ?? ''}</span>
      {/* Slack-style: show the :shortcode: being completed (emoji.id), not the
          pretty name — it explains why a row matched the typed query and
          teaches the shortcut for next time. The matched substring is
          highlighted; keyword-only matches show the matching keyword muted. */}
      <span className='text-sm text-primary truncate'>
        {shortcodeParts ? (
          <>
            :
            <HighlightedMatch parts={shortcodeParts} />:
          </>
        ) : (
          `:${emoji.id}:`
        )}
      </span>
      {keywordParts && (
        <span className='text-xs text-tertiary truncate'>
          <HighlightedMatch parts={keywordParts} />
        </span>
      )}
    </InlineComboboxItem>
  );
}

export function EmojiInputElement({
  attributes,
  children,
  element,
}: PlateElementProps) {
  const editor = useEditorRef();
  const [value, setValue] = useState('');
  const [data, setData] = useState<EmojiMartData | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadEmojiData()
      .then(loaded => {
        if (!cancelled) {
          setData(loaded);
        }
      })
      .catch(() => {
        /* already warned in loadEmojiData */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const emojis = useMemo(() => searchEmojis(data, value), [data, value]);
  const handleSelect = useCallback(
    (emoji: Emoji) => {
      insertEmoji(editor, emoji);
    },
    [editor]
  );

  return (
    <span {...attributes}>
      <InlineCombobox
        element={element}
        // Preceding-whitespace / non-trigger boundary matching (so `10:30` and
        // `https://` don't open the popover) comes entirely from
        // @platejs/emoji's BaseEmojiPlugin default options
        // (options.triggerPreviousCharPattern = /^\s?$/, from
        // BaseEmojiPlugin.ts, wired in unmodified via
        // EmojiPlugin = toPlatePlugin(BaseEmojiPlugin, ...) in
        // emoji-editor-plugins.ts), not custom code here — the same
        // unconfigured-default reliance as MentionInputPlugin in
        // PlateConversationEditor.tsx. The end-to-end popover behavior isn't
        // unit-tested here (Plate input nodes don't instantiate reliably in
        // JSDOM — see this file's test file top-of-file comment), but the
        // boundary regex itself is unit-tested directly against
        // @platejs/emoji's BaseEmojiPlugin export in
        // EmojiInputElement.test.tsx.
        trigger=':'
        filter={false}
        hideWhenNoValue
        value={value}
        setValue={setValue}
      >
        <InlineComboboxInput />
        <InlineComboboxContent className='bg-primary border border-primary rounded-xl shadow-lg max-h-72 overflow-y-auto min-w-[240px]'>
          {emojis.map(emoji => (
            <EmojiComboboxItem
              key={emoji.id}
              emoji={emoji}
              onSelect={handleSelect}
              query={value}
            />
          ))}
        </InlineComboboxContent>
      </InlineCombobox>
      {children}
    </span>
  );
}
